import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useVinScanner } from '@/hooks/useVinScanner';
import { useUpdateVehicleVin } from '@/hooks/useUpdateVehicleVin';
import { useVinDecoder } from '@/hooks/useVinDecoder';
import { isEmbeddedMode } from '@/utils/embeddedMode';
import { VinDecodeResult } from '@/api/vinScanner';

interface VinScannerScreenProps {
  onClose: () => void;
  onVinScanned: (vin: string) => void;
  vehicleId?: string;
}

interface VinScanResult {
  vinFound: boolean;
  confidence: number;
  vin: string;
}

const VinScannerScreen: React.FC<VinScannerScreenProps> = ({ onClose, onVinScanned, vehicleId }) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<VinScanResult | null>(null);
  const [vehicleInfo, setVehicleInfo] = useState<VinDecodeResult | null>(null);
  const [editedVin, setEditedVin] = useState<string | null>(null);
  const [isEditingVin, setIsEditingVin] = useState(false);
  
  // Check if we're in embedded mode
  const isEmbedded = isEmbeddedMode();

  // Refs for camera and canvas
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // VIN scanning hook
  const { scanImage, isScanning, error, clearError } = useVinScanner();

  // VIN decoding hook
  const { decodeVin, isDecoding: isDecodingVin } = useVinDecoder();

  // VIN update mutation
  const updateVehicleVinMutation = useUpdateVehicleVin();

  // Initialize camera stream
  useEffect(() => {
    let isMounted = true;
    
    const initializeCamera = async () => {
      try {
        const constraints = {
          video: {
            facingMode: 'environment', // Use rear camera
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        
        if (!isMounted) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        streamRef.current = stream;
        
        if (videoRef.current && isMounted) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error accessing camera:', error);
          clearError();
        }
      }
    };

    initializeCamera();

    return () => {
      isMounted = false;
      cleanup();
    };
  }, []);

  const cleanup = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
  };

  const capturePhoto = () => {
    // Check if we're in embedded mode (Android app)
    const isEmbeddedMode = !document.querySelector('[data-testid="hamburger-menu"]');
    
    if (isEmbeddedMode && typeof (window as any).capturePhoto === 'function') {
      console.log('Using native Android camera for photo capture');
      setIsCapturing(true);
      
      // Set up callback for when photo is captured
      (window as any).onPhotoCaptured = (photoData: string) => {
        console.log('Photo captured from Android, processing...');
        setCapturedImage(`data:image/jpeg;base64,${photoData}`);
        processImageWithBackend(`data:image/jpeg;base64,${photoData}`);
        setIsCapturing(false);
      };
      
      (window as any).onPhotoError = (error: string) => {
        console.error('Photo capture error from Android:', error);
        setIsCapturing(false);
      };
      
      // Call the native Android camera
      (window as any).capturePhoto();
      return;
    }
    
    // Fallback to web camera
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d')!;

      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get image data from canvas
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageDataUrl);
      
      // Process the image with backend
      processImageWithBackend(imageDataUrl);
    } catch (error) {
      console.error('Error capturing photo:', error);
              // Error will be handled by the hook
      setIsCapturing(false);
    }
  };

  const processImageWithBackend = async (imageDataUrl: string) => {
    try {
      const result = await scanImage(imageDataUrl);
      
      if (result) {
        setScanResult(result);
        
        if (result.vinFound && result.vin) {
          console.log('VIN detected:', result.vin);

          // Decode VIN to get vehicle info
          const decodedInfo = await decodeVin(result.vin);
          setVehicleInfo(decodedInfo);
        } else {
          console.log('No VIN found in image');
          setVehicleInfo(null);
        }
      }
    } catch (error) {
      console.error('Error processing image with backend:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleUseVin = () => {
    if (scanResult?.vinFound) {
      // Use edited VIN if available, otherwise use scanned VIN
      const vinToUse = editedVin || scanResult.vin;
      
      if (vehicleId) {
        // Pass vehicle info along with VIN so it updates in the cache
        updateVehicleVinMutation.mutate(
          { 
            vehicleId, 
            vin: vinToUse,
            // Include decoded vehicle info if available
            ...(vehicleInfo?.make && { vehicleName: vehicleInfo.make }),
            ...(vehicleInfo?.model && { vehicleModel: vehicleInfo.model }),
            ...(vehicleInfo?.year && { vehicleYear: vehicleInfo.year }),
          },
          {
            onSuccess: () => {
              console.log('VIN and vehicle info updated successfully in database');
              onVinScanned(vinToUse);
            },
            onError: (error) => {
              console.error('Failed to update VIN in database:', error);
              onVinScanned(vinToUse);
            },
          }
        );
      } else {
        onVinScanned(vinToUse);
      }
    }
  };

  const handleRetry = () => {
    setCapturedImage(null);
    setScanResult(null);
    setVehicleInfo(null);
    setEditedVin(null);
    setIsEditingVin(false);
    clearError();
  };

  const handleClose = () => {
    cleanup();
    // Clean up Android photo capture callbacks
    delete (window as any).onPhotoCaptured;
    delete (window as any).onPhotoError;
    onClose();
  };

  // Set up Android navigation callbacks
  useEffect(() => {
    // Only set up navigation in embedded mode
    if (typeof (window as any).ReactNativeWebView !== 'undefined') {
      console.log('📱 [VinScannerScreen] Setting up Android navigation callbacks');
      
      (window as any).navigateBack = function() {
        console.log('📱 [VinScannerScreen] navigateBack called - going back to CallScreen');
        handleClose();
      };
      
      (window as any).navigateForward = function() {
        console.log('📱 [VinScannerScreen] navigateForward called - no action (forward not applicable)');
        // Do nothing - forward navigation not applicable on VinScannerScreen
      };
      
      // Cleanup function
      return () => {
        console.log('📱 [VinScannerScreen] Cleaning up Android navigation callbacks');
        delete (window as any).navigateBack;
        delete (window as any).navigateForward;
      };
    }
  }, [handleClose]);

  return (
    <div className="fixed inset-0 bg-background overflow-y-auto">
      <div className="responsive-container mx-auto flex flex-col min-h-screen">
        {/* Header - Hide camera preview in embedded mode when no photo taken */}
        {!(isEmbedded && !capturedImage) && (
          <div className="relative">
            {/* Camera Stream Area */}
            <div className="w-full h-80 bg-black relative overflow-hidden">
              {!capturedImage ? (
                <>
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    autoPlay
                    playsInline
                    muted
                  />
                  
                  {/* Camera guide overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-4/5 h-24 border-2 border-white rounded-lg overflow-hidden">
                      <div className="absolute inset-0 bg-background/10"></div>
                      {/* Corner markers */}
                      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-500"></div>
                      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-500"></div>
                      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-500"></div>
                      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-500"></div>
                    </div>
                  </div>
                </>
              ) : (
                <img 
                  src={capturedImage} 
                  alt="Captured VIN" 
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Close button */}
              <button 
                onClick={handleClose}
                className="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center"
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            {/* Hidden canvas for processing */}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        )}
        
        {/* Close button for embedded mode when camera preview is hidden */}
        {isEmbedded && !capturedImage && (
          <div className="relative">
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 bg-gray-600 bg-opacity-50 rounded-full flex items-center justify-center z-10"
            >
              <X size={20} className="text-white" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 px-4 py-6">
          {/* Title */}
          <h1 className="text-xl font-semibold text-gray-900 mb-6">VIN SCANNER</h1>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertCircle size={20} className="text-red-600" />
                <p className="text-sm font-medium text-red-900">{error}</p>
              </div>
            </div>
          )}

          {/* Processing Status */}
          {isScanning && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="animate-spin text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Processing Image</p>
                  <p className="text-xs text-blue-600">Analyzing with AI...</p>
                </div>
              </div>
            </div>
          )}

          {/* VIN Result */}
          {scanResult && (
            <div className="mb-6">
              {scanResult.vinFound ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-green-600" />
                      <p className="text-sm font-medium text-green-900">VIN Detected</p>
                    </div>
                    <p className="text-sm font-medium text-green-900">Confidence: {scanResult.confidence}%</p>
                  </div>
                  <div className="bg-background rounded p-3 mb-3">
                    {isEditingVin ? (
                      <input
                        type="text"
                        value={editedVin || scanResult.vin}
                        onChange={(e) => setEditedVin(e.target.value)}
                        onBlur={() => setIsEditingVin(false)}
                        autoFocus
                        className="w-full font-mono text-lg text-center rounded px-2 py-1 border-2 border-green-400"
                        style={{
                          color: '#166534',
                          backgroundColor: '#ffffff',
                        }}
                      />
                    ) : (
                      <p 
                        onClick={() => setIsEditingVin(true)}
                        className="font-mono text-lg text-center cursor-pointer rounded px-2 py-1 transition-colors"
                        style={{
                          color: '#166534',
                          backgroundColor: '#ffffff',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                      >
                        {editedVin || scanResult.vin}
                      </p>
                    )}
                  </div>

                  {/* Vehicle Info Display */}
                  {vehicleInfo && (
                    <div className="bg-background rounded p-3 mb-3">
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><span className="font-medium">Year:</span> {vehicleInfo.year || 'N/A'}</p>
                        <p><span className="font-medium">Make:</span> {vehicleInfo.make || 'N/A'}</p>
                        <p><span className="font-medium">Model:</span> {vehicleInfo.model || 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {/* Loading state for VIN decoding */}
                  {isDecodingVin && (
                    <div className="mt-3 text-center">
                      <Loader2 size={16} className="animate-spin text-green-600 inline mr-2" />
                      <span className="text-sm text-green-600">Getting vehicle details...</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertCircle size={20} className="text-yellow-600" />
                    <div>
                      <p className="text-sm font-medium text-yellow-900">No VIN Found</p>
                      <p className="text-xs text-yellow-600">Confidence: {scanResult.confidence}%</p>
                    </div>
                  </div>
                  <p className="text-sm text-yellow-800">
                    No VIN was detected in this image. Try taking a clearer photo or adjusting the angle.
                  </p>
                </div>
              )}
            </div>
          )}

         

          {/* Action Buttons */}
          <div className="space-y-3">
            {!capturedImage ? (
              <button
                onClick={capturePhoto}
                disabled={isCapturing || isScanning}
                className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Camera size={20} />
                {isCapturing ? 'Capturing...' : 'Capture Photo'}
              </button>
            ) : scanResult?.vinFound ? (
              <button
                onClick={handleUseVin}
                disabled={updateVehicleVinMutation.isPending}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {updateVehicleVinMutation.isPending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Updating VIN...
                  </>
                ) : (
                  'Use This VIN'
                )}
              </button>
            ) : null}

            {capturedImage && (
              <button
                onClick={handleRetry}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Take New Photo
              </button>
            )}
          </div>
        </div>

        {/* Bottom Status */}
        <div className="bg-gray-100 px-4 py-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {isScanning 
                ? 'Processing with AI...'
                : capturedImage 
                  ? scanResult?.vinFound 
                    ? 'VIN Found ✓'
                    : 'No VIN Detected'
                  : 'Ready to capture'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VinScannerScreen; 
